import React, { cloneElement, type FC, type ReactElement, type ReactNode } from 'react'
import {
    Box,
    InputAdornment,
    type InputLabelProps as MuiInputLabelProps,
    TextField as MuiTextField,
    type TextFieldProps as MuiTextFieldProps,
    type OutlinedInputProps,
    type SxProps,
    type Theme,
    Tooltip,
} from '@mui/material'

import { copyObjectWithoutKeys } from '../../../../utils'

import { Translation } from '../../../../domain'

import { HelpOutlineIcon } from '../Branding'
import { Caption } from '../Typography'

/**
 * MUI types each slot prop as a union covering every hypothetical `component` override.
 * `Extract` narrows each one down to the real props of the component this file always renders.
 */
type MuiInputSlotProps = NonNullable<MuiTextFieldProps<'outlined'>['slotProps']>
type InputSlotProps = Readonly<{
    input?: Extract<MuiInputSlotProps['input'], OutlinedInputProps>
    inputLabel?: Extract<MuiInputSlotProps['inputLabel'], MuiInputLabelProps>
}>

export type TextFieldProps = Omit<MuiTextFieldProps<'outlined'>, 'variant' | 'size' | 'margin' | 'slotProps' | 'InputProps' | 'InputLabelProps'> &
    Readonly<{
        /**
         * If passed, renders a help icon with a tooltip containing `helpTooltip`
         */
        helpTooltip?: ReactNode

        /**
         * Renders a right-aligned "Optional" tag on the label row.
         * Ignored while `required` is set, since a field can't be both.
         */
        optional?: boolean

        startIcon?: ReactElement
        endIcons?: readonly ReactElement[]

        /**
         * Renders icon in front of `MuiTextField.helperText`
         */
        helperTextIcon?: ReactElement

        slotProps?: InputSlotProps
    }>

const labelRowStyle: SxProps<Theme> = { display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between', gap: 1 }
const textStyle: SxProps<Theme> = { display: 'inline-flex', alignItems: 'center' }
const asteriskStyle: SxProps<Theme> = { color: 'error.600', ml: 0.5 }
const helperTextIconStyle: SxProps<Theme> = { mr: 1 }
const helpIconStyle: SxProps<Theme> = { ml: 1, verticalAlign: 'middle', color: 'grey.500' }

/**
 * Overrides SvgIcon's default 1.5rem to 1rem, which would otherwise tower over the input text.
 */
const defaultIconStyle: SxProps<Theme> = { fontSize: '1rem' }
const StyledIcon: FC<{ icon: ReactElement, extraSx?: SxProps<Theme> }> = ({ icon, extraSx }) =>
    cloneElement(icon, { sx: { ...defaultIconStyle, ...extraSx, ...icon.props.sx } })

const InputLabel: FC<Pick<TextFieldProps, 'label' | 'required' | 'optional' | 'helpTooltip'>> = ({ label, required, optional, helpTooltip }) => (
    <Box component="span" sx={labelRowStyle}>
        <Box component="span" sx={textStyle}>
            {label}
            {required && (
                /**
                 * MuiFormLabel's native asterisk lands after the help icon, not the text,
                 * so we render our own and disable the native one via slotProps.inputLabel.required
                 */
                <Box component="span" aria-hidden sx={asteriskStyle}>
                    *
                </Box>
            )}
            {helpTooltip && (
                <Tooltip title={helpTooltip} placement="top">
                    <Box component="span" sx={textStyle}>
                        <HelpOutlineIcon sx={[defaultIconStyle, helpIconStyle]} />
                    </Box>
                </Tooltip>
            )}
        </Box>
        {optional && !required && <Caption id={Translation.OPTIONAL} />}
    </Box>
)

export const TextField: FC<TextFieldProps> = ({ label, startIcon, endIcons, helperText, helperTextIcon, slotProps, ...props }) => (
    <MuiTextField
        variant="outlined"
        label={label && <InputLabel label={label} {...props} />}
        helperText={
            helperText && (
                <Box component="span" sx={textStyle}>
                    {helperTextIcon && <StyledIcon icon={helperTextIcon} extraSx={helperTextIconStyle} />}
                    {helperText}
                </Box>
            )
        }
        {...copyObjectWithoutKeys(props, 'helpTooltip', 'optional')}
        slotProps={{
            input: {
                ...slotProps?.input,
                startAdornment: startIcon && (
                    <InputAdornment position="start">
                        <StyledIcon icon={startIcon} />
                    </InputAdornment>
                ),
                endAdornment: endIcons && endIcons.length > 0 && (
                    <InputAdornment position="end">
                        {endIcons.map((icon, index) => (
                            <StyledIcon key={index} icon={icon} />
                        ))}
                    </InputAdornment>
                ),
            },
            inputLabel: {
                ...slotProps?.inputLabel,

                /**
                 * Disables MuiFormLabel's native "required" asterisk:
                 * InputLabel above renders its own, correctly positioned.
                 */
                required: false,
            },
        }}
    />
)
